import * as React from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";

export interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Whether to show time selection */
  showTime?: boolean;
  /** Minimum selectable date */
  min?: Date;
  /** Maximum selectable date */
  max?: Date;
}

function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatTimeForInput(date: Date): string {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function formatDateTimeForInput(date: Date): string {
  return `${formatDateForInput(date)}T${formatTimeForInput(date)}`;
}

const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    { value, onChange, placeholder, className, disabled, showTime, min, max },
    ref
  ) => {
    const inputType = showTime ? "datetime-local" : "date";

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (!val) {
        onChange?.(undefined);
        return;
      }
      const date = new Date(val);
      if (!isNaN(date.getTime())) {
        onChange?.(date);
      }
    };

    const inputValue = value
      ? showTime
        ? formatDateTimeForInput(value)
        : formatDateForInput(value)
      : "";

    return (
      <div className={cn("relative", className)}>
        <Input
          ref={ref}
          type={inputType}
          value={inputValue}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          min={min ? formatDateForInput(min) : undefined}
          max={max ? formatDateForInput(max) : undefined}
          className="pr-10"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
          {showTime ? (
            <Clock className="h-4 w-4" />
          ) : (
            <Calendar className="h-4 w-4" />
          )}
        </div>
      </div>
    );
  }
);
DatePicker.displayName = "DatePicker";

export interface DateRangePickerProps {
  startDate?: Date;
  endDate?: Date;
  onStartDateChange?: (date: Date | undefined) => void;
  onEndDateChange?: (date: Date | undefined) => void;
  onChange?: (range: { start?: Date; end?: Date }) => void;
  startLabel?: string;
  endLabel?: string;
  className?: string;
  disabled?: boolean;
  showTime?: boolean;
  /** Preset ranges to show as quick selection buttons */
  showPresets?: boolean;
}

type PresetKey = "today" | "yesterday" | "last7days" | "last30days" | "thisMonth" | "lastMonth";

const presetKeys: PresetKey[] = ["today", "yesterday", "last7days", "last30days", "thisMonth", "lastMonth"];

const presetRangeGetters: Record<PresetKey, () => { start: Date; end: Date }> = {
  today: () => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { start, end };
  },
  yesterday: () => {
    const start = new Date();
    start.setDate(start.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setDate(end.getDate() - 1);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  },
  last7days: () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  },
  last30days: () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  },
  thisMonth: () => {
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { start, end };
  },
  lastMonth: () => {
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  },
};

const fallbackPresetLabels: Record<PresetKey, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last7days: "Last 7 days",
  last30days: "Last 30 days",
  thisMonth: "This month",
  lastMonth: "Last month",
};

const DateRangePicker = React.forwardRef<HTMLDivElement, DateRangePickerProps>(
  (
    {
      startDate,
      endDate,
      onStartDateChange,
      onEndDateChange,
      onChange,
      startLabel,
      endLabel,
      className,
      disabled,
      showTime,
      showPresets = true,
    },
    ref
  ) => {
    const { t } = useTranslation("common");

    const getPresetLabel = (key: PresetKey): string => {
      const translated = t(`reports.presets.${key}`, { defaultValue: "" });
      if (translated && !translated.startsWith("reports.")) {
        return translated;
      }
      return fallbackPresetLabels[key];
    };

    const handleStartChange = (date: Date | undefined) => {
      onStartDateChange?.(date);
      onChange?.({ start: date, end: endDate });
    };

    const handleEndChange = (date: Date | undefined) => {
      onEndDateChange?.(date);
      onChange?.({ start: startDate, end: date });
    };

    const handlePresetClick = (key: PresetKey) => {
      const { start, end } = presetRangeGetters[key]();
      onStartDateChange?.(start);
      onEndDateChange?.(end);
      onChange?.({ start, end });
    };

    const fromLabel = startLabel || t("labels.from", { defaultValue: "From" });
    const toLabel = endLabel || t("labels.to", { defaultValue: "To" });

    return (
      <div ref={ref} className={cn("space-y-3", className)}>
        {showPresets && (
          <div className="flex flex-wrap gap-2">
            {presetKeys.map((key) => (
              <Button
                key={key}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handlePresetClick(key)}
                disabled={disabled}
              >
                {getPresetLabel(key)}
              </Button>
            ))}
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 space-y-1.5">
            <Label className="text-sm text-muted-foreground">{fromLabel}</Label>
            <DatePicker
              value={startDate}
              onChange={handleStartChange}
              disabled={disabled}
              showTime={showTime}
              max={endDate}
            />
          </div>
          <div className="flex-1 space-y-1.5">
            <Label className="text-sm text-muted-foreground">{toLabel}</Label>
            <DatePicker
              value={endDate}
              onChange={handleEndChange}
              disabled={disabled}
              showTime={showTime}
              min={startDate}
            />
          </div>
        </div>
      </div>
    );
  }
);
DateRangePicker.displayName = "DateRangePicker";

export { DatePicker, DateRangePicker, presetRangeGetters };
export type { PresetKey };
